import moment from 'moment-timezone';
import { match } from 'ts-pattern';
import { Quarter } from './api/timeranges/types';
import {
  Granularity,
  Months,
  TimeSliderGranularity,
} from './common/components/timeslider/types';
import { Languages } from './constants';
import { Timestamp } from './types';

export const monthStart = (d: Timestamp) =>
  getMomentDate(d).startOf('month').startOf('day').valueOf();
export const monthEnd = (d: Timestamp) =>
  getMomentDate(d).endOf('month').startOf('day').valueOf();
export const yearEnd = (d: Timestamp) =>
  monthEndUtc(d).endOf('year').startOf('day').valueOf();
export const yearStart = (d: Timestamp) =>
  monthEnd(monthEndUtc(d).startOf('year').valueOf());
export const quarterStart = (d: Timestamp) =>
  monthEnd(monthEndUtc(d).startOf('quarter').valueOf());

// zero based index
export const getMonthIndex = (month: Months): number =>
  getMomentDate(month, 'MMMM').month();

export const getFiscalQuarter = (d: Timestamp, firstMonthOfYear: Months) => {
  const fiscalStartIndex = getMonthIndex(firstMonthOfYear); // 0-based
  const currentgetMonthIndex = getMomentDate(d).month(); // 0-based
  // Offset from fiscal year start, always positive (0-11)
  const offset = (currentgetMonthIndex - fiscalStartIndex + 12) % 12;
  // 0-based quarter, add 1 for 1-based
  return Math.floor(offset / 3) + 1;
};

export const getFiscalYear = (d: Timestamp, firstMonthOfYear: Months) => {
  const firstMonthNum = getMonthIndex(firstMonthOfYear);
  const dateAsMoment = getMomentDate(d);
  const month = dateAsMoment.month();
  const year = dateAsMoment.year();

  const fyYear = month < firstMonthNum ? year - 1 : year;
  return fyYear;
};

export const fiscalYearStart = (d: Timestamp, firstMonthOfYear: Months) => {
  const firstMonthNum = getMonthIndex(firstMonthOfYear);
  const fyYear = getFiscalYear(d, firstMonthOfYear);
  const fyMonth = firstMonthNum;
  const fyDay = 1;
  const fyDate = dateAt(fyYear, fyMonth, fyDay, d);
  return fyDate.valueOf();
};

export const startForGranularity = (
  d: Timestamp,
  granularity: Granularity,
  firstMonthOfYear: Months
) => {
  const firstMonthOfYearIndex = Object.keys(Months).indexOf(firstMonthOfYear);
  return match(granularity)
    .with(Granularity.MONTH, () => monthStart(d))
    .with(Granularity.YEAR, () => monthStart(yearStart(d)))
    .with(Granularity.FINQUARTER, () =>
      monthStart(addMonth(quarterStart(d), firstMonthOfYearIndex))
    )
    .with(Granularity.FINYEAR, () =>
      monthStart(addMonth(yearStart(d), firstMonthOfYearIndex))
    )
    .otherwise(() => {
      throw new Error('Unsupported granularity');
    });
};

export const endForGranularity = (
  d: Timestamp,
  granularity: TimeSliderGranularity,
  firstMonthOfYear: Months
) => {
  const firstMonthOfYearIndex = Object.keys(Months).indexOf(firstMonthOfYear);
  return match(granularity)
    .with({ value: Granularity.MONTH }, () => monthEnd(d))
    .with({ value: Granularity.YEAR }, () => monthEnd(yearStart(d)))
    .with({ value: Granularity.FINQUARTER }, () =>
      monthEnd(addMonth(quarterStart(d), firstMonthOfYearIndex))
    )
    .with({ value: Granularity.FINYEAR }, () =>
      monthEnd(addMonth(yearStart(d), firstMonthOfYearIndex))
    )
    .otherwise(() => {
      throw new Error('Unsupported granularity');
    });
};

export const isSame = (d1: Timestamp, d2: Timestamp) =>
  getMomentDate(d1).isSame(getMomentDate(d2), 'day');
export const isBeforeOrSame = (d1: Timestamp, d2: Timestamp) =>
  getMomentDate(d1).isSameOrBefore(getMomentDate(d2), 'day');
export const isBefore = (d1: Timestamp, d2: Timestamp) =>
  getMomentDate(d1).isBefore(getMomentDate(d2), 'day');

export const removeDay = (d: Timestamp, count: number) =>
  getMomentDate(d).add(-count, 'day').valueOf();
export const addDay = (d: Timestamp, count: number) =>
  getMomentDate(d).add(count, 'day').valueOf();
export const removeMonth = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.MONTH, -count);
export const addMonth = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.MONTH, count);
export const removeQuarter = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.MONTH, -count * 3);
export const addQuarter = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.MONTH, count * 3);
export const removeYear = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.YEAR, -count);
export const addYear = (d: Timestamp, count: number) =>
  dateShiftedByPeriods(d, Granularity.YEAR, count);

const dateShiftedByPeriods = (
  d: Timestamp,
  granularity: Granularity.MONTH | Granularity.YEAR,
  count: number
) => {
  const originalDate = getMomentDate(d);
  const isStartOfMonth = originalDate.date() === 1;
  const isEndOfMonth = originalDate.date() === originalDate.daysInMonth();
  let newDate = getMomentDate(d).add(count, granularity).valueOf();
  if (isStartOfMonth) {
    newDate = getMomentDate(newDate).startOf(Granularity.MONTH).valueOf();
  } else if (isEndOfMonth) {
    newDate = getMomentDate(newDate).endOf(Granularity.MONTH).valueOf();
  } else {
    newDate = getMomentDate(newDate).valueOf();
  }
  return newDate;
};

export const dateShiftedByYears = (d: Timestamp, count: number) => {
  return dateShiftedByPeriods(d, Granularity.YEAR, count);
};

export const dateShiftedByMonths = (d: Timestamp, count: number) => {
  return dateShiftedByPeriods(d, Granularity.MONTH, count);
};

export const format = (d: Timestamp) => {
  // All internal calculations are based on Japanese timezone (Asia/Tokyo).
  // We format dates according to Japanese time for consistency.
  return getMomentDate(d).format('YYYY-MM-DD');
};

export const formatYear = (d: number, locale: Languages): string => {
  switch (locale) {
    case Languages.EN:
      return d.toString();
    case Languages.JA:
      return `${d}年`;
    default:
      throw new Error('Unsupported locale');
  }
};
export const formatFinYear = (d: number, locale: Languages): string => {
  switch (locale) {
    case Languages.EN:
      return `FY${d}`;
    case Languages.JA:
      return `FY${d}`;
    default:
      throw new Error('Unsupported locale');
  }
};
export const formatFinQuarter = (d: Quarter, locale: Languages): string => {
  switch (locale) {
    case Languages.EN:
      return `${formatFinYear(d.year, locale)} Q${d.quarterOfYear}`;
    case Languages.JA:
      return `${formatFinYear(d.year, locale)} Q${d.quarterOfYear}`;
    default:
      throw new Error('Unsupported locale');
  }
};

export const formatMonth = (d: Date, locale: Languages) => {
  switch (locale) {
    case Languages.EN:
      return getMomentDate(d).format(`MMM 'YY`);
    case Languages.JA:
      return getMomentDate(d).format(`'YY年M月`);
    default:
      throw new Error('Unsupported locale');
  }
};

export const formatWithTimezone = (d: Timestamp, tz = 'Asia/Tokyo') => {
  return getMomentDate(d).tz(tz).format('YYYY-MM-DD HH:mm z');
};

export const formatFromNow = (d: Timestamp) => {
  // local uses the user timezone to convert the date before formatting it.
  // All internal calculations are based on UTC. We only convert to the user local timezone
  // when displaying dates and/or when requesting data through backend queries
  return getMomentDate(d).local().fromNow();
};

export const monthEndUtc = (d: Timestamp) => getMomentDate(monthEnd(d));

export const now = () => {
  return Date.now();
};

export const getLastMonthEndDate = () => format(monthEnd(addMonth(now(), -1)));

export const getMomentDate = (
  inp?: moment.MomentInput,
  format?: moment.MomentFormatSpecification,
  strict?: boolean
) => {
  // TODO: Right now we force all dates to be in Japan timezone,
  // even when local timezone is different.
  // We should revisit this decision and think about how we want to handle this
  const JAPAN_TIMEZONE = 'Asia/Tokyo';
  if (inp === undefined || inp === null) {
    return moment.tz(JAPAN_TIMEZONE);
  }

  // If format is provided, we assume inp is a string for parsing
  if (format && typeof inp === 'string') {
    return moment.tz(inp, format, strict || false, JAPAN_TIMEZONE);
  }

  // For all other cases (Date, number, moment), convert to timezone
  return moment.tz(inp, JAPAN_TIMEZONE);
};

export const dateAt = (
  year: number,
  month: number,
  day: number,
  current: Timestamp = now()
) => getMomentDate(current).year(year).month(month).date(day);
